import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ClipboardList, RotateCcw } from "lucide-react";
import { useI18n } from "../../../i18n";
import { useAuth } from "../../../context/AuthContext";
import { useCart } from "../../../context/CartContext";
import { fmtDate, fmtMoney } from "../../../lib/format";
import { useOrder, useShop } from "../hooks";
import { reorderInto } from "../reorder";
import { StatusBadge } from "../../../components/common/StatusBadge";
import { StatusTimeline } from "../orders/StatusTimeline";
import { ApprovalActions } from "../orders/ApprovalActions";
import { EmptyState, SkeletonBlock } from "../../../components/common/EmptyState";
import { DeliveryProof } from "../orders/DeliveryProof";
import { uomLabel } from "../../../lib/uom";

const Info = ({ label, value }) => (
  <div><dt className="text-xs text-slate-500">{label}</dt><dd className="mt-0.5 text-sm font-medium text-navy">{value}</dd></div>
);

const Lines = ({ order, shop }) => {
  const { t, lang } = useI18n();
  return (
    <ul className="divide-y divide-slate-100" data-testid="order-lines">
      {order.lines.map((l) => (
        <li key={`${l.line_no}-${l.item_code}-${l.uom}`} className="flex gap-3 py-3 text-sm">
          <div className="min-w-0 flex-1">
            <p className="font-medium text-navy">{shop.byCode[l.item_code]?.[`name_${lang}`] || l.item_code}</p>
            <p className="text-xs text-slate-500">{l.item_code} · {l.qty_boxes} {uomLabel(t, l.uom)} × {fmtMoney(l.net_price)}</p>
          </div>
          <span className="font-semibold tabular-nums text-navy">{fmtMoney(l.qty_boxes * l.net_price)}</span>
        </li>
      ))}
      <li className="space-y-1 pt-3 text-sm">
        <div className="flex justify-between text-slate-600"><span>{t("checkout.net")}</span><span className="tabular-nums">{fmtMoney(order.net_total)}</span></div>
        <div className="flex justify-between text-slate-600"><span>{t("checkout.vat")}</span><span className="tabular-nums">{fmtMoney(order.vat_total)}</span></div>
        <div className="flex justify-between text-base font-bold text-navy"><span>{t("checkout.gross")}</span><span className="tabular-nums" data-testid="order-gross">{fmtMoney(order.gross_total)}</span></div>
      </li>
    </ul>
  );
};

export default function OrderDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { t } = useI18n();
  const { user } = useAuth();
  const cart = useCart();
  const shop = useShop();
  const q = useOrder(id);
  const order = q.data;

  if (q.isLoading) return <SkeletonBlock className="h-96" />;
  if (!order) return <EmptyState icon={ClipboardList} title={t("orders.notFound")} testId="order-not-found" action={<Link to="/app/orders" className="text-sm font-semibold text-brand">{t("orders.title")}</Link>} />;
  const canApprove = order.status === "pending_approval" && user.permissions.includes("orders:approve");

  return (
    <div className="space-y-5" data-testid="order-detail">
      <button type="button" onClick={() => nav(-1)} data-testid="order-back" className="flex h-11 items-center gap-1 text-sm font-medium text-slate-600 hover:text-navy"><ArrowLeft className="h-4 w-4" /> {t("shop.back")}</button>
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-2xl font-bold text-navy sm:text-3xl" data-testid="order-doc-no">{order.softone_doc_no || t(order.status === "pending_approval" ? "orders.pendingNoDoc" : `status.${order.status}`)}</h1>
        <StatusBadge status={order.status} testId="order-status-badge" customer />
      </div>
      <div className="grid gap-5 md:grid-cols-[1fr_300px]">
        <div className="space-y-5">
          <dl className="grid grid-cols-1 gap-4 rounded-2xl bg-white p-4 shadow-sm sm:grid-cols-2 [&_dd]:break-words">
            <Info label={t("orders.orderDate")} value={fmtDate(order.order_date)} />
            <Info label={t("orders.deliveryDate")} value={fmtDate(order.requested_delivery_date)} />
            <Info label={t("orders.createdBy")} value={order.created_by} />
            {order.notes && <Info label={t("orders.notes")} value={order.notes} />}
          </dl>
          {canApprove && <ApprovalActions order={order} />}
          {order.reject_message && <p data-testid="order-reject-message" className="rounded-2xl bg-rose-50 p-4 text-sm text-rose-800">{order.reject_message}</p>}
          {(order.status === "delivered" || order.status === "invoiced") && order.local_id && <DeliveryProof localId={order.local_id} />}
          <section className="rounded-2xl bg-white px-4 pb-4 pt-3 shadow-sm"><h2 className="text-base font-semibold text-navy md:text-lg">{t("orders.lines")}</h2><Lines order={order} shop={shop} /></section>
          <button type="button" onClick={() => reorderInto(cart, order.lines, shop, t) && nav("/app/cart")} data-testid="order-reorder-button"
            className="flex h-12 w-full items-center justify-center gap-2 rounded-full bg-navy text-sm font-semibold text-white"><RotateCcw className="h-4 w-4" /> {t("home.reorder")}</button>
        </div>
        <section className="self-start rounded-2xl bg-white p-4 shadow-sm"><h2 className="mb-3 text-base font-semibold text-navy md:text-lg">{t("orders.timeline")}</h2><StatusTimeline status={order.status} /></section>
      </div>
    </div>
  );
}
